/* eslint-disable no-unused-vars */
/* eslint-disable max-lines */
import React, {
    useCallback, useEffect, useMemo, useRef, useState
} from 'react';
import {
    useNavigate, useParams, useSearchParams
} from 'react-router-dom';
import {
    opcodeAssistantService
} from 'shared/api';
import {
    Button
} from 'shared/components/ui/button';
import {
    DSButton, DSBadge
} from '../ui';
import {
    AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
    AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle
} from 'shared/components/ui/alert-dialog';
import {
    ChevronLeft, Save, Trash2, Loader2, AlertCircle, FlaskConical, Rocket, Code, RefreshCw
} from 'lucide-react';
import {
    cn
} from 'shared/lib/utils';
import ProvisionDialog from '../lib/ProvisionDialog';
import TestDock from '../components/TestDock';
import EntityJsonView from '../components/EntityJsonView';
import Section from './sections/Section';
import IdentitySection from './sections/IdentitySection';
import InstructionsSection from './sections/InstructionsSection';
import OpCodesSection from './sections/OpCodesSection';
import DataConnectionsSection from './sections/DataConnectionsSection';
import TypedInputSection from './sections/TypedInputSection';
import InputMappingSection from './sections/InputMappingSection';
import useOpcodeMeta from './sections/useOpcodeMeta';
import ChannelsSection from './sections/ChannelsSection';
import MemorySection from './sections/MemorySection';

/**
 * AssistantWorkspace — DE Studio Unified Builder full-page workspace.
 *
 * Section order: Identity → Default Instruction → Typed Input → OpCodes →
 * Channels → Memory.
 *
 * Typed Input lives BEFORE OpCodes so the user declares the assistant's
 * context_keys / propensity_keys / intent / entity_schema first, then maps
 * each opcode's runtime inputs to those typed-input fields via
 * `opcodes[i].input_mapping` (per SCHEMAS.md §5).
 *
 * No standalone Knowledge section: `assistant.knowledge[]` is auto-populated
 * from collections referenced by every RAG step inside the wired opcodes
 * (see OpCodesSection.refreshMeta). APIs / Instructions / MCPs are
 * defined inline inside the OpCode editor; collections likewise.
 */
const SECTIONS = [
    {
        key: 'identity',
        label: 'Identity & Voice'
    },
    {
        key: 'instructions',
        label: 'Default Instruction'
    },
    {
        key: 'typed_input',
        label: 'Typed Input'
    },
    {
        key: 'opcodes',
        label: 'OpCodes'
    },
    {
        key: 'input_mapping',
        label: 'Input Mapping'
    },
    {
        key: 'data_connections',
        label: 'Data Connections'
    },
    {
        key: 'channels',
        label: 'Channels'
    },
    {
        key: 'memory',
        label: 'Memory'
    }
];

export default function AssistantWorkspace() {
    const {
        oragID, subOragID, assistantId
    } = useParams();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const opcodeQuery = searchParams.get('opcode');
    const [assistant, setAssistant] = useState(null);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    /* Master-detail navigation — one section open at a time, picked from the
     * left rail. Defaults to Identity (the section every assistant has). */
    const [selectedSection, setSelectedSection] = useState('identity');
    const [confirmDelete, setConfirmDelete] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [confirmRegen, setConfirmRegen] = useState(false);
    const [savedFlash, setSavedFlash] = useState(false);
    const [isDirty, setIsDirty] = useState(false);
    const [mode, setMode] = useState('gui');
    const [showDock, setShowDock] = useState(() => window.innerWidth >= 768);
    const [dockMaximized, setDockMaximized] = useState(false);
    const [provisionOpen, setProvisionOpen] = useState(false);
    const [showJson, setShowJson] = useState(false);

    // Fetch the DE. `silent` (the header refresh button) keeps the page and just
    // spins the icon; the initial load shows the full-page loading state.
    const reload = useCallback((silent = false) => {
        if (!oragID || !subOragID || !assistantId) return undefined;
        if (silent) setRefreshing(true); else setLoading(true);
        setError(null);
        return opcodeAssistantService.getOpcodeAssistant(oragID, subOragID, assistantId)
            .then((res) => {
                setAssistant(res?.data || null);
                setIsDirty(false);
            })
            .catch((err) => {
                console.error('Failed to load assistant:', err);
                setError(err?.response?.data?.message || err?.message || 'Load failed');
            })
            .finally(() => { setLoading(false); setRefreshing(false); });
    }, [oragID, subOragID, assistantId]);

    useEffect(() => { reload(); }, [reload]);

    useEffect(() => {
        if (opcodeQuery) setSelectedSection('opcodes');
    }, [opcodeQuery]);

    function clearOpcodeQuery() {
        if (!searchParams.get('opcode')) return;
        const next = new URLSearchParams(searchParams);
        next.delete('opcode');
        setSearchParams(next, {
            replace: true
        });
    }

    /* Kept as a no-op for backwards compatibility with any callers that
     * still reference `tog`. The new UI is master-detail; sections are
     * selected, not toggled. */
    function tog(key) {
        setSelectedSection(key);
    }

    function patch(updates) {
        setAssistant((a) => ({
            ...a,
            ...updates
        }));
        setIsDirty(true);
    }

    // Single shared opcode-meta cache used by BOTH OpCodesSection (for card
    // rendering) AND InputMappingSection (for the per-input rows). Lifted
    // here so a `patchMeta` from one section's inline-save (e.g. the user
    // adding a new typed input to an opcode) immediately reflects in the
    // mapping section. If we let each section call useOpcodeMeta itself,
    // each gets its own cache and reactivity breaks across them.
    const opcodeMetaApi = useOpcodeMeta(assistant?.opcodes || []);
    const [expandedOpcodeId, setExpandedOpcodeId] = useState(null);
    const handleExpandedOpcodeChange = useCallback((id) => {
        setExpandedOpcodeId(id || null);
    }, []);
    const expandedOpcode = useMemo(() => {
        if (!expandedOpcodeId) return null;
        const doc = opcodeMetaApi.getDoc?.(expandedOpcodeId);
        if (doc) return doc;
        const meta = opcodeMetaApi.meta?.[expandedOpcodeId];
        if (!meta) return {
            opcode_id: expandedOpcodeId
        };
        return {
            opcode_id: expandedOpcodeId,
            title: meta.title,
            inputs: meta.inputs || []
        };
    }, [expandedOpcodeId, opcodeMetaApi.meta, opcodeMetaApi.docs]);

    // Imperative handle to the currently-mounted InlineOpcodeEditor (only
    // one is mounted at a time per OpCodesSection's `expanded` state). Lets
    // handleSave flush any pending opcode edits BEFORE saving the assistant
    // doc — single Save action, all writes persisted.
    const inlineOpcodeRef = useRef(null);
    const [opcodeTestRefresh, setOpcodeTestRefresh] = useState(0);

    async function handleSave() {
        if (!assistant) return;
        setSaving(true);
        setError(null);
        try {
            // Flush any pending opcode edits first — clicking Save on the
            // assistant must persist EVERYTHING, including unsaved inline
            // opcode changes. APIs / Instructions / MCPs / Channels created
            // via inline pickers persist on their own form save and are
            // already in Redis by the time we get here.
            const flushResult = await inlineOpcodeRef.current?.flushSave?.();
            if (flushResult && !flushResult.ok) {
                const m = flushResult.error?.response?.data?.message
                    || flushResult.error?.response?.data?.error
                    || flushResult.error?.message
                    || 'Failed to save the open opcode';
                setError(`Opcode save failed (${m}). Fix the opcode and retry.`);
                return;
            }
            await opcodeAssistantService.updateOpcodeAssistant({
                ...assistant,
                assistantId: assistant.assistant_id,
            });
            setSavedFlash(true);
            setIsDirty(false);
            setTimeout(() => setSavedFlash(false), 2000);
        } catch (err) {
            setError(err?.response?.data?.message || err?.message || 'Save failed');
        } finally {
            setSaving(false);
        }
    }

    async function handleDelete() {
        if (!assistant) return;
        setIsDeleting(true);
        setError(null);
        try {
            await opcodeAssistantService.deleteOpcodeAssistant({
                organizationId: oragID,
                subOrganizationId: subOragID,
                assistantId: assistant.assistant_id
            });
            navigate(`/organizations/${oragID}/sub/${subOragID}/de-studio/unified-builder`);
        } catch (err) {
            setError(err?.response?.data?.message || err?.message || 'Delete failed');
            setConfirmDelete(false);
        } finally {
            setIsDeleting(false);
        }
    }

    function handleRegenerateToken() {
        const newToken = 'sk-ast-' + Math.random().toString(36).slice(2, 18) + Math.random().toString(36).slice(2, 10);
        patch({
            assistant_token: newToken
        });
        setConfirmRegen(false);
    }

    if (loading) {
        return (
            <div className="flex h-full items-center justify-center p-8">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
        );
    }
    if (error && !assistant) {
        return (
            <div className="p-6">
                <div className="flex items-start gap-2 rounded-md border border-red-300 bg-red-50 px-4 py-3 text-red-900">
                    <AlertCircle className="mt-0.5 h-5 w-5" />
                    <div>
                        <div className="font-semibold">Could not load assistant</div>
                        <div className="text-sm">{error}</div>
                    </div>
                </div>
                <Button
                    variant="outline"
                    className="mt-4"
                    onClick={() => navigate(`/organizations/${oragID}/sub/${subOragID}/de-studio/unified-builder`)}
                >
                    <ChevronLeft className="mr-1 h-4 w-4" /> Back to Workforce
                </Button>
            </div>
        );
    }
    if (!assistant) return null;

    const live = assistant.status === 'active' && !assistant.disabled;

    return (
        <div className="flex h-full w-full flex-col" style={{
            background: 'var(--ds-bg)'
        }}>
            {/* ── Workspace header ─────────────────────────────── */}
            <header className="ds-ws-header">
                {/* Row 1: back + title — always full width on mobile */}
                <div className="ds-ws-header-title-row">
                    <button
                        type="button"
                        onClick={() => navigate(`/organizations/${oragID}/sub/${subOragID}/de-studio/unified-builder`)}
                        className="ds-icon-btn"
                        title="Back to Workforce"
                        aria-label="Back to Workforce"
                    >
                        <ChevronLeft size={16} />
                    </button>
                    <div style={{
                        flex: 1,
                        minWidth: 0
                    }}>
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 8,
                            minWidth: 0
                        }}>
                            <h1 className="ds-ws-title">
                                {assistant.title || assistant.assistant_id}
                            </h1>
                            <button
                                type="button"
                                onClick={() => reload(true)}
                                disabled={refreshing || loading}
                                title="Reload this Digital Employee"
                                className="ds-ws-refresh"
                                style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    background: 'none',
                                    border: 'none',
                                    cursor: 'pointer',
                                    padding: 4,
                                    color: 'var(--ds-muted, #6b6480)',
                                }}
                            >
                                <RefreshCw size={16} className={refreshing ? 'animate-spin' : ''} />
                            </button>
                            <DSBadge tone={live ? 'success' : 'neutral'}>{live ? 'Live' : 'Draft'}</DSBadge>
                        </div>
                        <div className="ds-ws-subtitle">
                            {assistant.description || 'No description'} · v{assistant.version || '0.1.0'}
                        </div>
                    </div>
                </div>

                {/* Row 2: mode tabs + actions */}
                <div className="ds-ws-header-actions-row">

                    {/* mode segmented — hidden on mobile, only 1 active mode anyway */}
                    <div className="ds-product-tabs ds-hide-on-mobile" role="tablist">
                        <button
                            type="button"
                            role="tab"
                            aria-selected={mode === 'gui'}
                            onClick={() => setMode('gui')}
                            className={cn('ds-product-tab', mode === 'gui' && 'ds-product-tab-active')}
                        >
                            GUI Builder
                        </button>
                        <button
                            type="button"
                            role="tab"
                            disabled
                            aria-selected={false}
                            title="Coming soon"
                            className="ds-product-tab"
                            style={{
                                cursor: 'not-allowed',
                                opacity: 0.5
                            }}
                        >
                            Build with AI <span style={{
                                marginLeft: 4,
                                fontSize: 10,
                                fontWeight: 500,
                                opacity: 0.7
                            }}>· soon</span>
                        </button>
                    </div>

                    <DSButton variant="ghost" size="sm" onClick={() => setShowDock((v) => !v)} className="ds-hide-on-mobile" style={{
                        position: 'relative'
                    }}>
                        {showDock ? 'Hide Test' : 'Show Test'}
                        {isDirty && !showDock && (
                            <span style={{
                                position: 'absolute',
                                top: 4,
                                right: 4,
                                width: 7,
                                height: 7,
                                borderRadius: '50%',
                                background: '#f59e0b',
                                border: '1.5px solid #fff'
                            }} />
                        )}
                    </DSButton>
                    <DSButton variant="ghost" size="sm" onClick={() => setShowJson(true)}
                        title="View JSON">
                        <Code size={14} />
                        <span className="ds-btn-label">JSON</span>
                    </DSButton>
                    <DSButton variant="secondary" size="sm" onClick={handleSave} disabled={saving}
                        title={isDirty ? 'Save changes' : 'Save'}
                        style={isDirty ? {
                            boxShadow: '0 0 0 2px var(--ds-warning, #f59e0b)'
                        } : undefined}>
                        {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
                        <span className="ds-btn-label">Save{isDirty ? ' *' : ''}</span>
                    </DSButton>
                    <DSButton variant="ghost" size="sm" onClick={() => setProvisionOpen(true)}
                        title="Clone tenants from this assistant">
                        <Rocket size={14} />
                        <span className="ds-btn-label">Provision</span>
                    </DSButton>
                    <DSButton variant="ghost" size="sm" onClick={() => setConfirmDelete(true)}
                        title="Delete assistant"
                        style={{
                            color: 'var(--ds-danger)'
                        }}>
                        <Trash2 size={14} />
                        <span className="ds-btn-label">Delete</span>
                    </DSButton>
                </div>{/* end ds-ws-header-actions-row */}
            </header>{/* end ds-ws-header */}

            {savedFlash && (
                <div style={{
                    background: 'var(--ds-success-soft)',
                    color: 'var(--ds-success)',
                    padding: '6px 16px',
                    textAlign: 'center',
                    fontSize: 'var(--ds-text-xs)',
                    fontWeight: 600
                }}>
                    ✓ Saved
                </div>
            )}
            <ProvisionDialog
                open={provisionOpen}
                assistant={{
                    assistant_id: assistantId,
                    title: assistant?.title
                }}
                organizationId={oragID}
                subOrganizationId={subOragID}
                onClose={() => setProvisionOpen(false)}
            />
            <EntityJsonView
                open={showJson}
                onOpenChange={setShowJson}
                title="Assistant JSON"
                entity={assistant}
                onApply={(parsed) => {
                    if (!parsed || typeof parsed !== 'object') return;
                    setAssistant(parsed);
                    setIsDirty(true);
                }}
            />
            {isDirty && !savedFlash && (
                <div className="ds-unsaved-banner" style={{
                    background: 'var(--ds-warning-soft, #fffbeb)',
                    borderBottom: '1px solid var(--ds-warning-border, #fcd34d)',
                    color: 'var(--ds-warning-text, #92400e)',
                    padding: '6px 16px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    fontSize: 'var(--ds-text-xs)',
                    fontWeight: 500,
                    gap: 8,
                }}>
                    <span>⚠ You have unsaved changes — click <b>Save</b> to persist them.</span>
                    <button
                        type="button"
                        onClick={handleSave}
                        disabled={saving}
                        style={{
                            background: 'var(--ds-warning, #f59e0b)',
                            color: '#fff',
                            border: 'none',
                            borderRadius: 4,
                            padding: '2px 10px',
                            fontSize: 'var(--ds-text-xs)',
                            fontWeight: 600,
                            cursor: saving ? 'not-allowed' : 'pointer',
                            opacity: saving ? 0.6 : 1,
                        }}
                    >
                        {saving ? 'Saving…' : 'Save now'}
                    </button>
                </div>
            )}
            {error && (
                <div style={{
                    background: 'var(--ds-danger-soft)',
                    color: 'var(--ds-danger)',
                    padding: '6px 16px',
                    textAlign: 'center',
                    fontSize: 'var(--ds-text-xs)',
                    fontWeight: 600
                }}>
                    {error}
                </div>
            )}

            {/* Body — split: workspace + test dock.
                .ds-workspace-body is the mobile-aware class — at <768px,
                tokens.css flips it to flex-column AND positions the
                TestDock <aside> as a fixed overlay covering the screen
                so the user toggles between workspace and test surface
                via the Hide Test (X) button inside the dock header. */}
            <div className="ds-workspace-body flex flex-1 overflow-hidden">
                <div className={`flex flex-col md:flex-row flex-1 overflow-hidden ${dockMaximized && showDock ? 'hidden' : ''}`}>
                    {/* Left rail — section list (desktop only) */}
                    <aside className="hidden md:block w-56 flex-shrink-0 overflow-y-auto border-r border-border/60 bg-muted/20 p-3">
                        <div className="space-y-1">
                            {SECTIONS.map((s) => {
                                const active = selectedSection === s.key;
                                return (
                                    <button
                                        key={s.key}
                                        type="button"
                                        onClick={() => setSelectedSection(s.key)}
                                        className={`flex w-full items-center justify-between gap-2 rounded-md border px-3 py-2 text-left text-sm transition ${active
                                            ? 'border-primary bg-primary/5 font-semibold text-foreground shadow-sm'
                                            : 'border-transparent text-foreground/85 hover:border-border hover:bg-card hover:text-foreground'
                                        }`}
                                    >
                                        <span className="truncate">{s.label}</span>
                                        {s.informational && (
                                            <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-amber-800">
                                                info
                                            </span>
                                        )}
                                    </button>
                                );
                            })}
                        </div>
                    </aside>

                    {/* Mobile section picker — replaces the desktop rail at <md.
                        Native <select> keeps it lightweight, accessible, and
                        works on every browser without a popover library. */}
                    <div className="md:hidden border-b border-border/60 bg-muted/20 px-3 py-2">
                        <label className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                            Section
                        </label>
                        <select
                            value={selectedSection}
                            onChange={(e) => setSelectedSection(e.target.value)}
                            className="mt-1 w-full rounded-md border border-border bg-card px-3 py-2 text-sm font-medium text-foreground"
                        >
                            {SECTIONS.map((s) => (
                                <option key={s.key} value={s.key}>
                                    {s.label}{s.informational ? ' · info' : ''}
                                </option>
                            ))}
                        </select>
                    </div>

                    {/* Right pane — selected section's body */}
                    <div className="flex-1 overflow-y-auto p-3 md:p-6">
                        <div className="w-full">
                            {(() => {
                                const s = SECTIONS.find((x) => x.key === selectedSection) || SECTIONS[0];
                                return (
                                    <div
                                        className="rounded-lg border bg-card shadow-sm"
                                        style={{
                                            border: `1px solid ${s.informational ? 'var(--ds-warning-soft)' : 'var(--ds-border)'}`
                                        }}
                                    >
                                        <div className="flex items-center gap-2 border-b border-border/60 px-5 py-3">
                                            <h2 className="text-base font-semibold text-foreground">{s.label}</h2>
                                            {s.informational && (
                                                <span className="rounded bg-amber-100 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-amber-800">
                                                    informational in V1
                                                </span>
                                            )}
                                        </div>
                                        <div className="p-5">
                                            {s.key === 'identity' && (
                                                <IdentitySection
                                                    a={assistant}
                                                    onPatch={patch}
                                                    onRegenerateToken={() => setConfirmRegen(true)}
                                                />
                                            )}
                                            {s.key === 'instructions' && <InstructionsSection a={assistant} onPatch={patch} />}
                                            {s.key === 'typed_input' && <TypedInputSection a={assistant} onPatch={patch} opcodes={assistant.opcodes || []} />}
                                            {s.key === 'opcodes' && (
                                                <OpCodesSection
                                                    a={assistant}
                                                    onPatch={patch}
                                                    opcodeMetaApi={opcodeMetaApi}
                                                    inlineOpcodeRef={inlineOpcodeRef}
                                                    initialExpandedOpcodeId={opcodeQuery}
                                                    onExpandedOpcodeConsumed={clearOpcodeQuery}
                                                    onExpandedOpcodeChange={handleExpandedOpcodeChange}
                                                    onOpcodeSaved={() => setOpcodeTestRefresh((k) => k + 1)}
                                                />
                                            )}
                                            {s.key === 'input_mapping' && <InputMappingSection a={assistant} onPatch={patch} opcodeMeta={opcodeMetaApi.meta} />}
                                            {s.key === 'data_connections' && <DataConnectionsSection a={assistant} onPatch={patch} />}
                                            {s.key === 'channels' && <ChannelsSection a={assistant} onPatch={patch} assistantId={assistant.assistant_id} />}
                                            {s.key === 'memory' && <MemorySection a={assistant} onPatch={patch} />}
                                        </div>
                                    </div>
                                );
                            })()}
                        </div>
                    </div>
                </div>
                {showDock && (
                    <TestDock
                        assistant={assistant}
                        expandedOpcode={expandedOpcode}
                        opcodeMeta={opcodeMetaApi.meta}
                        defaultTestMode={selectedSection === 'opcodes' ? 'opcode' : 'assistant'}
                        maximized={dockMaximized}
                        onMaximizeToggle={() => setDockMaximized((v) => !v)}
                        onClose={() => setShowDock(false)}
                        isDirty={isDirty}
                        onSaveFirst={handleSave}
                        refreshSignal={opcodeTestRefresh}
                    />
                )}
                {/* Mobile FAB — only visible on <768px */}
                {!showDock && (
                    <button
                        type="button"
                        className="ds-test-fab"
                        onClick={() => setShowDock(true)}
                        title="Open test panel"
                    >
                        <FlaskConical size={20} />
                        {isDirty && <span className="ds-test-fab-dot" />}
                    </button>
                )}
            </div>

            {/* Confirm delete dialog */}
            <AlertDialog
                open={confirmDelete}
                onOpenChange={(o) => { if (!o && !isDeleting) setConfirmDelete(false); }}
            >
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Delete this assistant?</AlertDialogTitle>
                        <AlertDialogDescription>
                            This will permanently delete <b>{assistant.title}</b> and break any active channel bindings.
                            This cannot be undone.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={(e) => { e.preventDefault(); handleDelete(); }}
                            disabled={isDeleting}
                            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                        >
                            {isDeleting ? (
                                <>
                                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                    Deleting…
                                </>
                            ) : 'Delete'}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            {/* Confirm regenerate token */}
            <AlertDialog open={confirmRegen} onOpenChange={setConfirmRegen}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Regenerate assistant token?</AlertDialogTitle>
                        <AlertDialogDescription>
                            Any existing A2A callers using the current token will stop working until updated.
                            Continue?
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction onClick={handleRegenerateToken}>Regenerate</AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}
